import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { healthAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import OrgSwitcher from './OrgSwitcher';

interface TopBarProps {
  onMenuClick: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const [dbStatus, setDbStatus] = useState<'ok' | 'error' | 'loading'>('loading');
  const { user, logout } = useAuth();

  useEffect(() => {
    const checkHealth = () => {
      healthAPI.check()
        .then((res) => setDbStatus(res.data.checks.db === 'ok' ? 'ok' : 'error'))
        .catch(() => setDbStatus('error'));
    };
    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar sx={{ minHeight: 52, gap: 1 }}>
        <IconButton
          aria-label="open navigation"
          edge="start"
          onClick={onMenuClick}
          size="small"
          sx={{ display: { md: 'none' } }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        <OrgSwitcher />

        <Box sx={{ flexGrow: 1 }} />

        {/* Minimal status dot */}
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor:
              dbStatus === 'ok' ? '#059669' : dbStatus === 'error' ? '#dc2626' : '#94a3b8',
            flexShrink: 0,
          }}
          title={dbStatus === 'ok' ? 'Connected' : dbStatus === 'error' ? 'Disconnected' : 'Checking...'}
        />

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '7px',
                backgroundColor: '#0f172a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                {(user.display_name || user.email)[0].toUpperCase()}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'text.secondary',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user.display_name || user.email}
            </Typography>
            <Button
              size="small"
              onClick={handleLogout}
              sx={{
                minHeight: 32,
                minWidth: 32,
                p: 0.5,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
              aria-label="Log out"
            >
              <LogoutIcon sx={{ fontSize: 16 }} />
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
